import { StandardEvents } from '@shopify/events';

const START_SECONDS = 5;
const REDIRECT_SECONDS = 2;

class CartRedirectNotice extends HTMLElement {
  #timer;
  #hideTimer;
  #sequence = 0;
  #previousFocus;

  connectedCallback() {
    this.innerHTML = `
      <section class="cart-success" aria-labelledby="cart-success-title" aria-describedby="cart-success-description">
        <button class="cart-success__close" type="button" aria-label="Close cart notification">&times;</button>
        <div class="cart-success__badge" aria-hidden="true">
          <span class="cart-success__halo"></span>
          <svg viewBox="0 0 48 48" focusable="false"><circle class="cart-success__circle" cx="24" cy="24" r="22" fill="none" stroke="currentColor" stroke-width="2"/><path class="cart-success__check" d="M15.5 24.2 21.4 30 32.8 18.4" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <div class="cart-success__label">Added to cart</div>
        <h2 class="cart-success__title" id="cart-success-title">Added successfully</h2>
        <p class="cart-success__description" id="cart-success-description">Your selection is now safely in your cart.</p>
        <div class="cart-success__product"><strong class="cart-success__name"></strong><span class="cart-success__quantity"></span></div>
        <div class="cart-success__count" aria-hidden="true">5</div>
        <p class="cart-success__timer" aria-live="off">Opening your cart in 5 seconds</p>
        <div class="cart-success__actions"><a class="cart-success__cart" href="/cart">View cart <span aria-hidden="true">→</span></a><button class="cart-success__shop" type="button">Keep shopping</button></div>
        <span class="cart-success__progress" aria-hidden="true"></span>
        <span class="cart-success__announcement" role="status" aria-live="polite"></span>
      </section>`;

    this.querySelector('.cart-success__cart').href = globalThis.Theme?.routes?.cart_url || '/cart';
    this.querySelector('.cart-success__close').addEventListener('click', () => this.hide());
    this.querySelector('.cart-success__shop').addEventListener('click', () => this.hide());
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.#stop();
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    if (event.action !== 'add' || !event.promise) return;
    const sequence = ++this.#sequence;
    event.promise.then(({ detail }) => {
      if (sequence !== this.#sequence || detail?.didError) return;
      const lines = event.lines || [];
      const ids = new Set(lines.map((line) => String(line.merchandiseId)));
      const items = detail?.items || [];
      const productId = String(detail?.productId || '');
      const item = items.find((entry) => ids.has(String(entry.variant_id || entry.id)))
        || items.find((entry) => productId && String(entry.product_id) === productId);
      const source = event.target instanceof Element ? event.target : null;
      const name = item?.product_title || item?.title
        || source?.closest('[data-product-title]')?.getAttribute('data-product-title')
        || source?.closest('product-form-component')?.querySelector('[data-product-title]')?.textContent?.trim()
        || document.querySelector('h1')?.textContent?.trim()
        || 'Added item';
      const quantity = Number(detail?.itemCount) || lines.reduce((total, line) => total + (Number(line.quantity) || 0), 0) || 1;
      this.show({ name, quantity });
    }).catch(() => {});
  };

  show({ name, quantity }) {
    this.#stop();
    this.#previousFocus = document.activeElement;
    this.querySelector('.cart-success__name').textContent = name;
    this.querySelector('.cart-success__quantity').textContent = `Quantity ${quantity}`;
    this.querySelector('.cart-success__announcement').textContent = `${name} added to your cart. Quantity ${quantity}.`;
    const count = this.querySelector('.cart-success__count');
    const timer = this.querySelector('.cart-success__timer');
    count.textContent = String(START_SECONDS);
    timer.textContent = `Opening your cart in ${START_SECONDS} seconds`;
    this.removeAttribute('hidden');
    this.removeAttribute('data-visible');
    requestAnimationFrame(() => {
      this.setAttribute('data-visible', 'true');
      this.querySelector('.cart-success__close').focus({ preventScroll: true });
    });
    let remaining = START_SECONDS;
    this.#timer = window.setInterval(() => {
      remaining -= 1;
      count.textContent = String(remaining);
      timer.textContent = remaining > REDIRECT_SECONDS ? `Opening your cart in ${remaining} seconds` : 'Opening your cart now…';
      if (remaining <= REDIRECT_SECONDS) {
        this.#stop();
        window.location.assign(globalThis.Theme?.routes?.cart_url || '/cart');
      }
    }, 1000);
  }

  hide() {
    this.#stop();
    this.removeAttribute('data-visible');
    this.#hideTimer = window.setTimeout(() => {
      this.setAttribute('hidden', '');
      if (this.#previousFocus instanceof HTMLElement) this.#previousFocus.focus({ preventScroll: true });
    }, 300);
  }

  #onKeydown = (event) => { if (event.key === 'Escape') this.hide(); };
  #stop() { window.clearInterval(this.#timer); window.clearTimeout(this.#hideTimer); }
}

if (!customElements.get('cart-redirect-notice')) customElements.define('cart-redirect-notice', CartRedirectNotice);
const notice = document.createElement('cart-redirect-notice');
notice.className = 'cart-redirect-notice';
notice.setAttribute('hidden', '');
document.body.appendChild(notice);
